// SubjectsDashboard.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import useClassesStore from '../../stateManagers/store/useClassesStore';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import AddEditSubjectModal, { SubjectRow, SubjectType, SubjectStatus } from './parts/AddEditSubjectModal';

type StatusFilter = 'all' | 'active' | 'inactive';

const SubjectsDashboard: React.FC = () => {
  const toast = useRef<Toast>(null);

  const { classOptions, fetchClasses, isFetching: classesFetching } = useClassesStore();
  const schoolDetails = useSchoolDetailsStore((s) => s.schoolDetails);
  const sessionId = schoolDetails?.current_session?.id ?? null;

  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [subjects, setSubjects] = useState<SubjectRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState<SubjectRow | null>(null);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [tableKey, setTableKey] = useState(0);

  useEffect(() => {
    fetchClasses(true);
  }, [fetchClasses]);

  const selectedClass = classOptions.find((c) => c.id === selectedClassId);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  useEffect(() => {
    clearSelection();
  }, [selectedClassId, statusFilter, clearSelection]);

  const fetchSubjects = useCallback(async () => {
    if (!selectedClassId) {
      setSubjects([]);
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, string> = { class_id: selectedClassId, status: statusFilter };
      if (sessionId) params.session_id = sessionId;

      const res = await apiClient.get('/subjects', { params });
      if (res.data.status === 'success') {
        const rows: SubjectRow[] = (res.data.subjects || []).map((s: any) => ({
          subject_id: s.subject_id,
          subject_name: s.subject_name,
          class_id: s.class_id,
          subject_type: s.subject_type,
          main_subject_id: s.main_subject_id || null,
          status: s.status,
        }));
        setSubjects(rows);
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch subjects', life: 4000 });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Network Error',
        detail: err.response?.data?.message || 'An error occurred while fetching subjects',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedClassId, statusFilter, sessionId]);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  const handleClassChange = (classId: string) => {
    setSelectedClassId((prev) => (prev === classId ? null : classId));
  };

  const handleDelete = async (subject: SubjectRow) => {
    if (!window.confirm(`Delete subject "${subject.subject_name}"? This will deactivate it.`)) return;
    try {
      const res = await apiClient.delete(`/subjects/${subject.subject_id}`);
      if (res.data.status === 'success') {
        toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Subject deactivated successfully!', life: 3500 });
        fetchSubjects();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Failed', detail: 'Failed to delete subject', life: 4000 });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'An error occurred',
        life: 5000,
      });
    }
  };

  const handleToggleStatus = async (subject: SubjectRow) => {
    const nextStatus: SubjectStatus = subject.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await apiClient.patch(`/subjects/${subject.subject_id}/status`, { status: nextStatus });
      if (res.data.status === 'success') {
        fetchSubjects();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Failed', detail: 'Failed to update status', life: 4000 });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'An error occurred',
        life: 5000,
      });
    }
  };

  const handleBulkStatus = async (status: SubjectStatus) => {
    try {
      const res = await apiClient.patch('/subjects/bulk-status', {
        subject_ids: Array.from(selectedIds),
        status,
      });
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Updated',
          detail: `${res.data.count ?? selectedIds.size} subject(s) updated to ${status}.`,
          life: 4000,
        });
        clearSelection();
        setTableKey((k) => k + 1);
        fetchSubjects();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Failed', detail: 'Bulk update failed', life: 4000 });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'An error occurred',
        life: 5000,
      });
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      subjects.forEach((s) => (checked ? next.add(s.subject_id) : next.delete(s.subject_id)));
      return next;
    });
  };

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => !prev);
    clearSelection();
    setTableKey((k) => k + 1);
  };

  const allChecked = subjects.length > 0 && subjects.every((s) => selectedIds.has(s.subject_id));

  const checkboxHeader = () => (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} style={cbStyle} />
    </div>
  );

  const checkboxTemplate = (row: SubjectRow) => (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <input
        type="checkbox"
        checked={selectedIds.has(row.subject_id)}
        onChange={() => toggleOne(row.subject_id)}
        onClick={(e) => e.stopPropagation()}
        style={cbStyle}
      />
    </div>
  );

  const serialTemplate = (_: SubjectRow, options: ColumnBodyOptions) => (
    <div
      style={{
        width: 28,
        height: 28,
        background: '#f9fafb',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 12,
        color: '#6b7280',
        margin: '0 auto',
      }}
    >
      {options.rowIndex + 1}
    </div>
  );

  const nameTemplate = (row: SubjectRow) => (
    <div style={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{row.subject_name}</div>
  );

  const typeColors: Record<SubjectType, { bg: string; color: string }> = {
    regular: { bg: '#eff6ff', color: '#1d4ed8' },
    optional: { bg: '#fdf4ff', color: '#a21caf' },
    combined: { bg: '#fff7ed', color: '#c2410c' },
  };

  const typeTemplate = (row: SubjectRow) => (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 8,
        fontSize: 11.5,
        fontWeight: 700,
        textTransform: 'capitalize',
        background: typeColors[row.subject_type]?.bg || '#f3f4f6',
        color: typeColors[row.subject_type]?.color || '#374151',
      }}
    >
      {row.subject_type}
    </span>
  );

  const mainSubjectTemplate = (row: SubjectRow) => {
    if (row.subject_type !== 'combined' || !row.main_subject_id) {
      return <span style={{ color: '#9ca3af', fontSize: 13 }}>—</span>;
    }
    const main = subjects.find((s) => s.subject_id === row.main_subject_id);
    return <span style={{ fontSize: 13, color: '#1f2937', fontWeight: 500 }}>{main?.subject_name || '—'}</span>;
  };

  const statusTemplate = (row: SubjectRow) => (
    <button
      onClick={() => handleToggleStatus(row)}
      style={{
        padding: '5px 12px',
        borderRadius: 20,
        fontSize: 11.5,
        fontWeight: 700,
        border: 'none',
        cursor: 'pointer',
        background: row.status === 'active' ? '#dcfce7' : '#fee2e2',
        color: row.status === 'active' ? '#15803d' : '#b91c1c',
      }}
      title="Click to toggle status"
    >
      {row.status === 'active' ? 'Active' : 'Inactive'}
    </button>
  );

  const actionsTemplate = (row: SubjectRow) => (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-rounded"
        tooltip="Edit"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#3b82f6', width: 32, height: 32 }}
        onClick={() => {
          setEditingSubject(row);
          setIsModalOpen(true);
        }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-rounded"
        tooltip="Delete"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#ef4444', width: 32, height: 32 }}
        onClick={() => handleDelete(row)}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <style>{`
          .p-datatable .p-datatable-tbody > tr:hover { background: rgba(65,105,225,.04) !important; }
          .p-datatable .p-datatable-thead > tr > th { background:#f9fafb!important; color:#6b7280!important; font-size:11px!important; font-weight:700!important; text-transform:uppercase!important; letter-spacing:.8px!important; padding:16px 12px!important; border-bottom:1px solid #e5e7eb!important; }
          .p-datatable .p-datatable-tbody > tr > td { padding:14px 12px!important; border-bottom:1px solid #f3f4f6!important; }
          .subj-class-chip { padding:6px 16px; border-radius:8px; font-size:11.5px; font-weight:600; cursor:pointer; border:1.5px solid #e5e7eb; background:white; color:#1f2937; transition: all .2s; }
          .subj-class-chip.active { border-color:#3b82f6; background:linear-gradient(135deg,#1e40af 0%,#3b82f6 50%,#60a5fa 100%); color:white; box-shadow:0 2px 8px rgba(59,130,246,.25); }
          .subj-status-chip { padding:6px 14px; border-radius:8px; font-size:11.5px; font-weight:600; cursor:pointer; border:1.5px solid #e5e7eb; background:white; color:#1f2937; }
          .subj-status-chip.active { border-color:#10b981; background:#10b981; color:white; }
          .bulk-action-bar { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; background:linear-gradient(135deg,#1e40af,#3b82f6); border-radius:14px; padding:12px 18px; margin-bottom:16px; box-shadow:0 4px 16px rgba(59,130,246,.3); }
          .bulk-count { font-size:13px; font-weight:700; color:white; }
          .bulk-action-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:10px; font-size:12px; font-weight:600; cursor:pointer; border:none; }
          .bulk-action-btn.primary { background:white; color:#1e40af; }
          .bulk-action-btn.danger { background:#fee2e2; color:#b91c1c; }
          .bulk-action-btn.ghost { background:rgba(255,255,255,.15); color:white; border:1px solid rgba(255,255,255,.3); }
          .select-mode-btn { display:inline-flex; align-items:center; gap:6px; padding:6px 14px; border-radius:10px; font-size:12px; font-weight:600; cursor:pointer; border:1.5px solid #c7d2fe; background:white; color:#4169E1; }
          .select-mode-btn.on { background:#4169E1; color:white; border-color:#4169E1; }
        `}</style>

        <GlobalHeaderTitle
          title="Subjects"
          subtitle={selectedClass ? `${subjects.length} subject(s) in ${selectedClass.label}` : 'Select a class to manage subjects'}
          icon="pi pi-book"
          buttons={[
            {
              label: 'Add Subject',
              icon: 'pi pi-plus',
              onClick: () => {
                setEditingSubject(null);
                setIsModalOpen(true);
              },
              disabled: !selectedClassId,
            },
          ]}
        />

        <div
          style={{
            background: 'white',
            borderRadius: '16px',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.12)',
          }}
        >
          <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: '#1e40af', marginBottom: '10px', letterSpacing: '0.02em' }}>
            Select Class
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {classesFetching ? (
              <div style={{ padding: '6px 14px', color: '#64748b', fontSize: '13px', fontWeight: 500 }}>Loading classes...</div>
            ) : classOptions.length === 0 ? (
              <div style={{ padding: '6px 14px', color: '#64748b', fontSize: '13px', fontWeight: 500 }}>No classes available</div>
            ) : (
              classOptions.map((cls) => (
                <button
                  key={cls.id}
                  className={`subj-class-chip ${selectedClassId === cls.id ? 'active' : ''}`}
                  onClick={() => handleClassChange(cls.id)}
                >
                  {cls.label}
                </button>
              ))
            )}
          </div>

          {selectedClassId && (
            <div style={{ paddingTop: '16px', marginTop: '16px', borderTop: '1px solid #f3f4f6' }}>
              <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: '#1e40af', marginBottom: '10px', letterSpacing: '0.02em' }}>
                Status
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {(['active', 'inactive', 'all'] as StatusFilter[]).map((sf) => (
                  <button
                    key={sf}
                    className={`subj-status-chip ${statusFilter === sf ? 'active' : ''}`}
                    onClick={() => setStatusFilter(sf)}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {sf}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {!selectedClassId ? (
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              border: '1px solid #e5e7eb',
              padding: '60px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <i className="pi pi-filter" style={{ fontSize: 25, color: '#3b82f6' }} />
            <p style={{ color: '#64748b', fontSize: 14, fontWeight: 500, margin: 0 }}>
              Select a class above to view and manage its subjects
            </p>
          </div>
        ) : (
          <>
            {selectedIds.size > 0 && (
              <div className="bulk-action-bar">
                <div className="bulk-count">
                  {selectedIds.size} subject{selectedIds.size !== 1 ? 's' : ''} selected
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="bulk-action-btn primary" onClick={() => handleBulkStatus('active')}>
                    <i className="pi pi-check-circle" style={{ fontSize: 12 }} /> Activate
                  </button>
                  <button className="bulk-action-btn danger" onClick={() => handleBulkStatus('inactive')}>
                    <i className="pi pi-ban" style={{ fontSize: 12 }} /> Deactivate
                  </button>
                  <button className="bulk-action-btn ghost" onClick={clearSelection}>
                    <i className="pi pi-times" style={{ fontSize: 12 }} /> Clear
                  </button>
                </div>
              </div>
            )}

            <div
              style={{
                background: 'white',
                borderRadius: 20,
                boxShadow: '0 2px 12px rgba(0,0,0,.06)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <button className={`select-mode-btn ${selectionMode ? 'on' : ''}`} onClick={toggleSelectionMode}>
                  <i className="pi pi-check-square" style={{ fontSize: 12 }} />
                  {selectionMode ? 'Exit Select' : 'Select'}
                </button>
              </div>

              <DataTable
                key={tableKey}
                value={subjects}
                loading={loading}
                emptyMessage={
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b', fontSize: 14, fontWeight: 500 }}>
                    No subjects found for this class{statusFilter !== 'all' ? ` (${statusFilter})` : ''}.
                  </div>
                }
              >
                {selectionMode && <Column header={checkboxHeader} body={checkboxTemplate} style={{ width: 52, textAlign: 'center' }} />}
                <Column header="#" body={serialTemplate} style={{ width: 52 }} />
                <Column header="Subject Name" body={nameTemplate} />
                <Column header="Type" body={typeTemplate} style={{ width: 120 }} />
                <Column header="Main Subject" body={mainSubjectTemplate} />
                <Column header="Status" body={statusTemplate} style={{ width: 110 }} />
                <Column header="Actions" body={actionsTemplate} style={{ width: 100 }} />
              </DataTable>
            </div>
          </>
        )}
      </div>

      {selectedClassId && (
        <AddEditSubjectModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={fetchSubjects}
          classId={selectedClassId}
          className={selectedClass?.label || ''}
          sessionId={sessionId}
          editingSubject={editingSubject}
          existingSubjects={subjects}
        />
      )}
    </>
  );
};

const cbStyle: React.CSSProperties = {
  width: 17,
  height: 17,
  cursor: 'pointer',
  accentColor: '#3b82f6',
};

export default SubjectsDashboard;
